import { useSyncExternalStore } from "react";
import { useTranslation } from "../../lib";
import { isSessionScopeCurrent } from "../../lib/auth/session_scope";
import { discardQuestionnaireDraft } from "../../lib/questionnaire_draft/coordinator";
import {
  dismissQuestionnaireCreateConflict,
  getQuestionnaireCreateConflict,
  subscribeQuestionnaireCreateConflict,
} from "../../lib/questionnaire_draft/create_conflict";
import { useSelectPrivateUiAllowed, useSelectTheme } from "../../store";
import { Button, ButtonText } from "..";

/**
 * Says why a questionnaire save created nothing: an earlier save of the same edit already created it,
 * or is still creating it. The recovery copy is never applied or deleted for the researcher; it is
 * kept, since it may hold edits made after that first save, unless they discard it here. Mounted once,
 * above the router, because the editor that sent the create may have closed before the answer came.
 */
export const QuestionnaireCreateConflictNotice = () => {
  const notice = useSyncExternalStore(
    subscribeQuestionnaireCreateConflict,
    getQuestionnaireCreateConflict,
  );
  const { t } = useTranslation();
  const { color, pad, fontSize, fontWeight, borderRadius } = useSelectTheme();
  const privateUiAllowed = useSelectPrivateUiAllowed();

  if (!notice || !isSessionScopeCurrent(notice.scope)) return null;
  // hidden, not dismissed: on an unverified session it waits for the session to be confirmed
  if (!privateUiAllowed) return null;

  let title: string;
  let messageList: string[];
  let discard: (() => void) | null = null;
  if (notice.kind === "already_created") {
    const { savedTask, copyKey, scope } = notice;
    title = t("questionnaire_draft.already_created_title");
    messageList = [
      savedTask
        ? // React escapes the text it renders, so i18next must not escape the name or the date as well
          t("questionnaire_draft.already_created_message", {
            name: savedTask.name,
            time: new Date(savedTask.updatedAt).toLocaleString(),
            interpolation: { escapeValue: false },
          })
        : t("questionnaire_draft.already_created_message_unnamed"),
      ...(copyKey ? [t("questionnaire_draft.already_created_copy_kept")] : []),
    ];
    if (copyKey) {
      discard = () => {
        discardQuestionnaireDraft(scope, copyKey);
        dismissQuestionnaireCreateConflict();
      };
    }
  } else {
    title = t("questionnaire_draft.create_in_progress_title");
    messageList = [t("questionnaire_draft.create_in_progress_message")];
  }

  return (
    // above the editor popups it may follow, and below the session warnings, which mount after it
    <div
      data-testid="questionnaire_create_conflict_backdrop"
      css={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 100000,
        background: "#00000040",
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="questionnaire_create_conflict_title"
        aria-describedby="questionnaire_create_conflict_message"
        css={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxSizing: "border-box",
          width: "480px",
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          padding: pad.xl,
          borderRadius: borderRadius.default,
          background: color.white,
          color: color.textIcon.title,
        }}
      >
        <h2
          id="questionnaire_create_conflict_title"
          css={{ margin: 0, fontSize: fontSize.h3, fontWeight: fontWeight.bold }}
        >
          {title}
        </h2>
        <div
          id="questionnaire_create_conflict_message"
          css={{ margin: `${pad.large} 0 ${pad.xl}` }}
        >
          {messageList.map((message) => (
            <p key={message} css={{ margin: `0 0 ${pad.mediumSmall}` }}>
              {message}
            </p>
          ))}
        </div>
        <div
          css={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: pad.large,
          }}
        >
          {discard && (
            <ButtonText onClick={discard}>{t("questionnaire_draft.discard_button")}</ButtonText>
          )}
          <Button type="button" autoFocus onClick={dismissQuestionnaireCreateConflict}>
            {t("questionnaire_draft.ok_button")}
          </Button>
        </div>
      </div>
    </div>
  );
};
